import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { TrendingUp, DollarSign, Target, Calendar } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from 'recharts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API = `/api`;

const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30d');
  const [revenueData, setRevenueData] = useState([]);
  const [pipelineData, setPipelineData] = useState([]);
  const [conversionData, setConversionData] = useState([]);
  const [forecastData, setForecastData] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics?time_range=${timeRange}`);
      const data = response.data;
      
      setRevenueData(data.monthly_revenue || []);
      setPipelineData(data.pipeline_by_stage || []);
      setConversionData(data.conversion_funnel || []);
      setForecastData(data.forecast || {});
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-zinc-400">Loading analytics...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Revenue Analytics & Forecasting
          </h2>
          <p className="text-sm text-zinc-400 mt-1">Track performance and predict future revenue</p>
        </div>
        <Select value={timeRange} onValueChange={setTimeRange}>
          <SelectTrigger data-testid="time-range-filter" className="w-full md:w-48 bg-zinc-900/50 border-zinc-800">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#121212] border-zinc-800">
            <SelectItem value="7d">Last 7 Days</SelectItem>
            <SelectItem value="30d">Last 30 Days</SelectItem>
            <SelectItem value="90d">Last 90 Days</SelectItem>
            <SelectItem value="1year">This Year</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div data-testid="analytics-total-revenue" className="bg-app-card border border-app-border rounded-lg p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-blue-500/10 p-2 rounded-md">
              <DollarSign className="w-5 h-5 text-blue-500" strokeWidth={1.5} />
            </div>
            <p className="text-xs text-app-subtext uppercase tracking-wider font-medium">Total Revenue</p>
          </div>
          <p className="text-3xl font-bold tracking-tight tabular-nums" style={{ fontFamily: 'Manrope, sans-serif' }}>
            ₹{(forecastData?.total_revenue || 0).toLocaleString('en-IN')}
          </p>
          <p className="text-xs text-green-500 mt-1">+{forecastData?.growth_rate || 0}% from last period</p>
        </div>

        <div className="bg-app-card border border-app-border rounded-lg p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-purple-500/10 p-2 rounded-md">
              <Target className="w-5 h-5 text-purple-500" strokeWidth={1.5} />
            </div>
            <p className="text-xs text-app-subtext uppercase tracking-wider font-medium">Pipeline Value</p>
          </div>
          <p className="text-3xl font-bold tracking-tight tabular-nums" style={{ fontFamily: 'Manrope, sans-serif' }}>
            ₹{(forecastData?.pipeline_value || 0).toLocaleString('en-IN')}
          </p>
          <p className="text-xs text-zinc-400 mt-1">Active deals in pipeline</p>
        </div>

        <div className="bg-app-card border border-app-border rounded-lg p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-yellow-500/10 p-2 rounded-md">
              <TrendingUp className="w-5 h-5 text-yellow-500" strokeWidth={1.5} />
            </div>
            <p className="text-xs text-app-subtext uppercase tracking-wider font-medium">Forecasted Revenue</p>
          </div>
          <p className="text-3xl font-bold tracking-tight tabular-nums text-yellow-500" style={{ fontFamily: 'Manrope, sans-serif' }}>
            ₹{(forecastData?.forecasted_next_month || 0).toLocaleString('en-IN')}
          </p>
          <p className="text-xs text-zinc-400 mt-1">Next 30 days projection</p>
        </div>

        <div className="bg-app-card border border-app-border rounded-lg p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-green-500/10 p-2 rounded-md">
              <Calendar className="w-5 h-5 text-green-500" strokeWidth={1.5} />
            </div>
            <p className="text-xs text-app-subtext uppercase tracking-wider font-medium">Avg Deal Size</p>
          </div>
          <p className="text-3xl font-bold tracking-tight tabular-nums" style={{ fontFamily: 'Manrope, sans-serif' }}>
            ₹{(forecastData?.avg_deal_size || 0).toLocaleString('en-IN')}
          </p>
          <p className="text-xs text-zinc-400 mt-1">Per closed deal</p>
        </div>
      </div>

      {/* Revenue Trend Chart */}
      <div className="bg-app-card border border-app-border rounded-lg p-6">
        <h3 className="text-xl font-semibold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Monthly Revenue Trend
        </h3>
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={revenueData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
            <XAxis dataKey="month" stroke="#71717A" style={{ fontSize: '12px' }} />
            <YAxis stroke="#71717A" style={{ fontSize: '12px' }} />
            <Tooltip
              contentStyle={{ backgroundColor: '#121212', border: '1px solid #27272A', borderRadius: '8px' }}
              labelStyle={{ color: '#FAFAFA' }}
            />
            <Legend />
            <Line type="monotone" dataKey="revenue" stroke="#3B82F6" strokeWidth={2} name="Revenue (₹)" dot={{ r: 4 }} />
            <Line type="monotone" dataKey="forecast" stroke="#F59E0B" strokeWidth={2} strokeDasharray="5 5" name="Forecast (₹)" dot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Pipeline by Stage */}
        <div className="bg-app-card border border-app-border rounded-lg p-6">
          <h3 className="text-xl font-semibold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Pipeline Distribution
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pipelineData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {pipelineData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ backgroundColor: '#121212', border: '1px solid #27272A', borderRadius: '8px' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Conversion Funnel */}
        <div className="bg-app-card border border-app-border rounded-lg p-6">
          <h3 className="text-xl font-semibold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Conversion Funnel
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={conversionData} layout="horizontal">
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
              <XAxis type="number" stroke="#71717A" style={{ fontSize: '12px' }} />
              <YAxis dataKey="stage" type="category" stroke="#71717A" style={{ fontSize: '12px' }} width={120} />
              <Tooltip
                contentStyle={{ backgroundColor: '#121212', border: '1px solid #27272A', borderRadius: '8px' }}
              />
              <Bar dataKey="count" fill="#3B82F6" radius={[0, 8, 8, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Forecast Insights */}
      <div className="bg-app-card border border-app-border rounded-lg p-6">
        <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Revenue Forecast & Insights
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-app-muted border border-zinc-800/50 rounded-md p-4">
            <p className="text-xs text-app-subtext mb-2">Expected Close This Month</p>
            <p className="text-2xl font-bold text-blue-500">₹{(forecastData?.expected_close_this_month || 0).toLocaleString('en-IN')}</p>
            <p className="text-xs text-zinc-400 mt-1">Based on deal probability</p>
          </div>
          <div className="bg-app-muted border border-zinc-800/50 rounded-md p-4">
            <p className="text-xs text-app-subtext mb-2">Win Rate</p>
            <p className="text-2xl font-bold text-green-500">{forecastData?.win_rate || 0}%</p>
            <p className="text-xs text-zinc-400 mt-1">Historical conversion rate</p>
          </div>
          <div className="bg-app-muted border border-zinc-800/50 rounded-md p-4">
            <p className="text-xs text-app-subtext mb-2">Deals Closing Soon</p>
            <p className="text-2xl font-bold text-yellow-500">{forecastData?.deals_closing_soon || 0}</p>
            <p className="text-xs text-zinc-400 mt-1">Within next 7 days</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
